import Axios from 'axios';
import type {
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios';
import type { SilentRequest } from '@azure/msal-browser';
import { msalInstance } from '@/auth';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuth?: boolean;
  }
}

Axios.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const request: SilentRequest = {
      scopes: ['openid']
    };

    if (config.skipAuth) {
      return config;
    }

    const accessTokenObject = await msalInstance.acquireTokenSilent(request);
    if (accessTokenObject?.accessToken && accessTokenObject?.tokenType) {
      const authHeader =
        accessTokenObject.tokenType + ' ' + accessTokenObject.accessToken || '';
      config.headers.setAuthorization(authHeader);
    }

    return config;
  },

  function (error) {
    console.error(error);
    return Promise.reject(error);
  }
);

async function get<T>(
  endpoint: string,
  options?: AxiosRequestConfig<unknown>
): Promise<AxiosResponse<T>> {
  return Axios.get<T>(endpoint, options);
}

async function post(
  endpoint: string,
  requestData: AxiosRequestConfig<unknown>,
  isBodyTypeJson = true
): Promise<AxiosResponse> {
  try {
    // Note: For a .NET backend a raw string to a controller will return 415 Unsupported Media Type.
    // To get around this we need to encapsulate the raw string in hyphens like below and send it as "Content-Type": "application/json"
    return isBodyTypeJson
      ? await Axios.post(endpoint, requestData)
      : await Axios.post(endpoint, "'" + requestData + "'", {
          headers: { 'Content-Type': 'application/json' }
        });
  } catch (error: unknown) {
    console.error(error);
    return error;
  }
}

async function put(endpoint: string, data?: unknown): Promise<AxiosResponse> {
  try {
    return await Axios.put(endpoint, data);
  } catch (error: unknown) {
    console.error(error);
    return error;
  }
}

async function patch(endpoint: string, data: unknown): Promise<AxiosResponse> {
  try {
    return await Axios.patch(endpoint, data);
  } catch (error: unknown) {
    console.error(error);
    return error;
  }
}

async function deleteResource(
  resource: string,
  data?: any
): Promise<AxiosResponse> {
  try {
    return await Axios.delete(resource, { data });
  } catch (error: unknown) {
    console.error(error);
    return error;
  }
}

export const apiService = {
  get,
  post,
  put,
  patch,
  deleteResource
};
